<template>
  <DraggableContainer
    storage-key="theme-toggle-position"
    v-slot="{ isDragging }"
  >
    <div class="theme-toggle-card" :class="{ 'dragging': isDragging }">
      <div class="theme-toggle-header">
        <div class="drag-handle" title="Drag to move">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="9" cy="12" r="1"/>
            <circle cx="9" cy="5" r="1"/>
            <circle cx="9" cy="19" r="1"/>
            <circle cx="15" cy="12" r="1"/>
            <circle cx="15" cy="5" r="1"/>
            <circle cx="15" cy="19" r="1"/>
          </svg>
        </div>
        <h3 class="theme-toggle-title">Theme Test</h3>
        <button class="minimize-btn" @click="toggleMinimized" title="Minimize">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
        </button>
      </div>

      <div v-show="!isMinimized" class="theme-toggle-content">
        <div class="theme-toggle-info">
          <span class="current-theme-label">Current:</span>
          <span class="current-theme-value">{{ getCurrentThemeDisplayName() }}</span>
        </div>

        <div class="theme-buttons">
          <button
            v-for="theme in themeOptions"
            :key="theme.identifier"
            @click="switchToTheme(theme.identifier)"
            :class="[
              'theme-btn',
              currentThemeIdentifier === theme.identifier ? 'theme-btn-active' : 'theme-btn-inactive'
            ]"
          >
            {{ theme.displayName }}
          </button>
        </div>
      </div>

      <!-- Color Preview -->
      <div v-show="!isMinimized" class="theme-preview">
        <div class="color-dot primary" title="Primary"></div>
        <div class="color-dot secondary" title="Secondary"></div>
        <div class="color-dot tertiary" title="Tertiary"></div>
      </div>
    </div>
  </DraggableContainer>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useWhitelabelTheme } from '@/composables/useWhitelabelTheme'
import DraggableContainer from '@/components/DraggableContainer.vue'

const { currentThemeIdentifier, switchToTheme } = useWhitelabelTheme()

const isMinimized = ref(false)

// Load saved minimized state from localStorage
onMounted(() => {
  const savedMinimized = localStorage.getItem('theme-toggle-minimized')
  if (savedMinimized) {
    isMinimized.value = JSON.parse(savedMinimized)
  }
})

const saveMinimizedState = () => {
  localStorage.setItem('theme-toggle-minimized', JSON.stringify(isMinimized.value))
}

const toggleMinimized = () => {
  isMinimized.value = !isMinimized.value
  saveMinimizedState()
}

// Define available theme options for testing
const themeOptions = [
  { identifier: 'otivo', displayName: 'Light' },
  { identifier: 'otivo-dark', displayName: 'Dark' },
  { identifier: 'test', displayName: 'Test' },
]

const getCurrentThemeDisplayName = () => {
  const theme = themeOptions.find(t => t.identifier === currentThemeIdentifier.value)
  return theme?.displayName || 'Unknown'
}
</script>

<style scoped>
.theme-toggle-card {
  background: var(--color-white);
  border: 2px solid var(--color-grey-4);
  border-radius: 12px;
  padding: 16px;
  box-shadow: var(--shadow-popup);
  min-width: 200px;
  backdrop-filter: blur(10px);
  transition: all 0.2s ease;
  font-family: var(--font-family-sans);
}

.theme-toggle-card.dragging {
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  transform: rotate(2deg) scale(1.02);
  border-color: var(--color-primary);
}

.theme-toggle-header {
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.drag-handle {
  color: var(--color-grey-3);
  cursor: grab;
  padding: 2px;
  border-radius: 4px;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.drag-handle:hover {
  color: var(--color-primary);
  background: var(--color-grey-5);
}

.drag-handle:active {
  cursor: grabbing;
}

.minimize-btn {
  background: none;
  border: none;
  color: var(--color-grey-3);
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.minimize-btn:hover {
  color: var(--color-primary);
  background: var(--color-grey-5);
}

.theme-toggle-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--color-grey-1);
  margin: 0;
  flex: 1;
  text-align: center;
}

.theme-toggle-content {
  margin-bottom: 12px;
}

.theme-toggle-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-bottom: 12px;
}

.current-theme-label {
  font-size: 11px;
  color: var(--color-grey-3);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.current-theme-value {
  font-size: 13px;
  font-weight: 500;
  color: var(--color-grey-1);
}

/* Theme Button Styles */
.theme-buttons {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.theme-btn {
  padding: 6px 12px;
  border-radius: 6px;
  border: 1px solid var(--color-grey-4);
  background: var(--color-white);
  color: var(--color-grey-1);
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  flex: 1;
  min-width: 50px;
}

.theme-btn-active {
  background: var(--color-primary);
  color: var(--color-white);
  border-color: var(--color-primary);
}

.theme-btn-inactive:hover {
  background: var(--color-grey-5);
  border-color: var(--color-grey-3);
}

/* Color Preview */
.theme-preview {
  display: flex;
  gap: 6px;
  justify-content: center;
  padding-top: 8px;
  border-top: 1px solid var(--color-grey-4);
}

.color-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 1px solid rgba(0,0,0,0.1);
}

.color-dot.primary {
  background-color: var(--color-primary);
}

.color-dot.secondary {
  background-color: var(--color-secondary);
}

.color-dot.tertiary {
  background-color: var(--color-tertiary);
}

/* Smooth minimize animation */
.theme-toggle-content,
.theme-preview {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
}

/* Hover effects */
.theme-toggle-card:hover:not(.dragging) {
  border-color: var(--color-primary);
  box-shadow: var(--shadow-light);
  transform: translateY(-1px);
  transition: all 0.2s ease;
}

/* Mobile touch improvements */
@media (max-width: 768px) {
  .theme-toggle-card {
    min-width: 180px;
    padding: 12px;
  }

  .theme-btn {
    padding: 8px 10px;
    font-size: 11px;
  }
}

/* Accessibility */
@media (prefers-reduced-motion: reduce) {
  .theme-toggle-card,
  .drag-handle,
  .minimize-btn {
    transition: none;
  }

  .theme-toggle-card.dragging {
    transform: none;
  }
}
</style>
